import type { TableRow, TimeSeriesData } from "@unbox/api";
import { Calendar } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Table, TableCard } from "@/components/application/table/table";
import { type DateGranularity, DateGranularitySelector } from "@/components/DateGranularitySelector";
import { MetricsBarChartView } from "@/components/MetricsBarChartView";
import { MetricsLineChartView } from "@/components/MetricsLineChartView";
import { MetricsTableView } from "@/components/MetricsTableView";
import { CREATOR_SEGMENT_COLORS, REVENUE_SOURCE_COLORS } from "@/constants/metrics";
import { useReportFormatters } from "@/hooks/use-report-formatters";
import { formatDateByPeriod } from "../utils";

interface TemplateReportViewProps {
  templateId: string;
  chartData: TimeSeriesData | TableRow[];
  reportName: string;
  startDate: Date;
  endDate: Date;
  createdAt: string;
  updatedAt: string;
  onExport?: (data: any, templateId: string, reportName: string) => void;
  chartElementRef?: React.RefObject<HTMLDivElement | null>;
}

export const TemplateReportView: React.FC<TemplateReportViewProps> = ({
  templateId,
  chartData,
  reportName,
  startDate,
  endDate,
  createdAt,
  updatedAt,
  onExport,
  chartElementRef,
}) => {
  const { gmv: gmvFormatter, number: numberFormatter } = useReportFormatters();

  // Internal state for granularity selection (defaults to daily)
  const [selectedGranularity, setSelectedGranularity] = useState<"daily" | "weekly" | "monthly">("daily");

  // Common footer component
  const footer = (
    <div className="flex items-center justify-between text-sm text-gray-500 flex-shrink-0">
      <div>
        Last edited:{" "}
        {new Date(updatedAt).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        }) +
          " @" +
          new Date(updatedAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })}
      </div>
      <div>
        Created:{" "}
        {new Date(createdAt).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        })}
      </div>
    </div>
  );

  // Common date range header (without granularity selector)
  const dateRangeHeader = (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <Calendar className="w-4 h-4" />
      <span>
        {startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} -{" "}
        {endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
      </span>
    </div>
  );

  // Date range header WITH granularity selector
  const dateRangeWithGranularity = (
    <div className="flex items-center justify-between flex-shrink-0">
      {dateRangeHeader}
      <DateGranularitySelector
        value={selectedGranularity === "daily" ? "day" : selectedGranularity === "weekly" ? "week" : "month"}
        onChange={(value: DateGranularity) => {
          setSelectedGranularity(value === "day" ? "daily" : value === "week" ? "weekly" : "monthly");
        }}
      />
    </div>
  );

  // Helper to create a legend with date/granularity controls for bar charts
  const createBarChartHeader = (config: {
    metrics: string[];
    labels: Record<string, string>;
    colors: Record<string, string>;
    displayOrder?: string[]; // Optional separate order for legend display
  }) => {
    const legendOrder = config.displayOrder || config.metrics;
    return (
      <div className="flex items-center justify-between flex-shrink-0">
        {/* Legend on the left */}
        <div className="flex items-center gap-6">
          {legendOrder.map((metric) => (
            <div key={metric} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.colors[metric] }}></div>
              <span className="text-base font-medium text-gray-600">{config.labels[metric]}</span>
            </div>
          ))}
        </div>

        {/* Date range and granularity on the right */}
        <div className="flex items-center gap-4">
          {dateRangeHeader}
          <DateGranularitySelector
            value={selectedGranularity === "daily" ? "day" : selectedGranularity === "weekly" ? "week" : "month"}
            onChange={(value: DateGranularity) => {
              setSelectedGranularity(value === "day" ? "daily" : value === "week" ? "weekly" : "monthly");
            }}
          />
        </div>
      </div>
    );
  };

  // Helper to create a legend with date/granularity controls for line/area charts
  const createLineChartHeader = (config: {
    metrics: string[];
    labels: Record<string, string>;
    colors: Record<string, string>;
  }) => (
    <div className="flex items-center justify-between flex-shrink-0">
      {/* Legend on the left */}
      <div className="flex items-center gap-6">
        {config.metrics.map((metric) => (
          <div key={metric} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.colors[metric] }}></div>
            <span className="text-sm font-medium text-gray-900">{config.labels[metric]}</span>
          </div>
        ))}
      </div>

      {/* Date range and granularity on the right */}
      <div className="flex items-center gap-4">
        {dateRangeHeader}
        <DateGranularitySelector
          value={selectedGranularity === "daily" ? "day" : selectedGranularity === "weekly" ? "week" : "month"}
          onChange={(value: DateGranularity) => {
            setSelectedGranularity(value === "day" ? "daily" : value === "week" ? "weekly" : "monthly");
          }}
        />
      </div>
    </div>
  );

  // Helper: Transform time series data for chart views
  const getTransformedData = (timeSeriesData: TimeSeriesData) => {
    const currentSeriesData = timeSeriesData[selectedGranularity];
    const dateMap = new Map<string, any>();

    currentSeriesData.forEach((series) => {
      series.data.forEach((point) => {
        if (!dateMap.has(point.date)) {
          dateMap.set(point.date, {
            date: point.date,
            dateLabel: formatDateByPeriod(new Date(point.date), selectedGranularity),
          });
        }

        // For creator-community-health, make dormant values negative
        // Convert to number first since API returns strings
        const numValue = Number(point.value);
        const value = templateId === "creator-community-health" && series.id === "dormant" ? -numValue : numValue;

        dateMap.get(point.date)[series.id] = value;
      });
    });

    return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Switch on template type to render appropriate view
  switch (templateId) {
    case "pattern-analysis": {
      // Custom table for hook pattern analysis (TableRow[])
      const tableData = chartData as TableRow[];

      if (!tableData || tableData.length === 0) {
        return (
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No data available for this date range</p>
              </div>
            </div>
          </div>
        );
      }

      // Transform TableRow[] to format expected by MetricsTableView
      const transformedData = tableData.map((row) => ({
        dateLabel: row.hook,
        gmv: row.gmv,
        videoCount: row.videoCount,
        videoPercentage: row.videoPercentage,
        viewCount: row.viewCount,
        viewPercentage: row.viewPercentage,
      }));

      const config = {
        metrics: ["gmv", "videoCount", "videoPercentage", "viewCount", "viewPercentage"],
        formatters: {
          gmv: (v: number) => gmvFormatter.format(v),
          videoCount: (v: number) => v.toString(),
          videoPercentage: (v: number) => `${v.toFixed(1)}%`,
          viewCount: (v: number) => numberFormatter.format(v),
          viewPercentage: (v: number) => `${v.toFixed(1)}%`,
        },
        labels: {
          gmv: "GMV",
          videoCount: "# Videos",
          videoPercentage: "Video %",
          viewCount: "# Views",
          viewPercentage: "View %",
        },
        colors: {},
      };

      // Notify parent of export data (update ref directly)
      if (onExport && transformedData.length > 0) {
        onExport(
          {
            data: transformedData,
            metrics: config.metrics,
            labels: config.labels,
          },
          templateId,
          reportName,
        );
      }

      return (
        <div className="flex flex-col h-full gap-4">
          <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4">
            {/* Header - date range on the right */}
            <div className="flex items-center justify-end flex-shrink-0">{dateRangeHeader}</div>

            {/* Table */}
            <div className="flex-1 min-h-0 overflow-auto">
              <MetricsTableView
                data={transformedData as any}
                config={config as any}
                orientation="column-wise"
                firstColumnLabel="Hook"
              />
            </div>
          </div>
          {footer}
        </div>
      );
    }

    case "content-analysis": {
      // Line chart for hook performance over time
      const timeSeriesData = chartData as TimeSeriesData;
      const transformedData = getTransformedData(timeSeriesData);

      if (transformedData.length === 0) {
        return (
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No data available for this date range</p>
              </div>
            </div>
          </div>
        );
      }

      // Get all hook names from the data
      const hookMetrics = Object.keys(transformedData[0]).filter((key) => key !== "date" && key !== "dateLabel");

      const config = {
        metrics: hookMetrics,
        formatters: Object.fromEntries(hookMetrics.map((m) => [m, (v: number) => gmvFormatter.format(v)])),
        colors: Object.fromEntries(
          hookMetrics.map((m, i) => [
            m,
            [
              "#3b82f6", // blue
              "#10b981", // green
              "#f59e0b", // amber
              "#ef4444", // red
              "#8b5cf6", // purple
            ][i % 5],
          ]),
        ),
        labels: Object.fromEntries(hookMetrics.map((m) => [m, m])),
      };

      return (
        <div className="flex flex-col h-full gap-4">
          <div
            ref={chartElementRef}
            className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4"
          >
            {/* Header with legend left, date/granularity right */}
            {createLineChartHeader(config)}

            {/* Chart */}
            <div className="flex-1 min-h-0">
              <MetricsLineChartView
                data={transformedData as any}
                config={config as any}
                showLegend={false}
                chartType="line"
              />
            </div>
          </div>
          {footer}
        </div>
      );
    }

    case "creator-community-health": {
      // Stacked bar chart with dormant overlay
      const timeSeriesData = chartData as TimeSeriesData;
      const transformedData = getTransformedData(timeSeriesData);

      if (transformedData.length === 0) {
        return (
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No data available for this date range</p>
              </div>
            </div>
          </div>
        );
      }

      // Desired display order for legend and tooltip: New → Reactivated → Active → Dormant
      // Recharts renders stacked bars in REVERSE order (last Bar component = bottom of stack)
      // So we reverse the array: last item (dormant) renders at bottom, first item (new) at top
      // But we want: New(bottom) → Reactivated → Active → Dormant(top)
      // So metrics should be: Dormant → Active → Reactivated → New
      const displayOrder = ["new", "reactivated", "active", "dormant"];
      const segmentMetrics = ["dormant", "active", "reactivated", "new"];

      const config = {
        metrics: segmentMetrics,
        displayOrder: displayOrder, // Use this for legend (not reversed)
        formatters: Object.fromEntries(
          displayOrder.map((m) => [m, (v: number) => numberFormatter.format(m === "dormant" ? Math.abs(v) : v)]),
        ),
        colors: CREATOR_SEGMENT_COLORS,
        labels: {
          new: "New",
          active: "Active",
          reactivated: "Reactivated",
          dormant: "Dormant",
        },
        stackIds: {
          new: "positive",
          active: "positive",
          reactivated: "positive",
          dormant: "negative",
        },
      };

      return (
        <div className="flex flex-col h-full gap-4">
          <div
            ref={chartElementRef}
            className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4"
          >
            {/* Header with legend left, date/granularity right */}
            {createBarChartHeader(config)}

            {/* Chart */}
            <div className="flex-1 min-h-0">
              <MetricsBarChartView
                data={transformedData as any}
                config={config as any}
                variant="stacked"
                showLegend={false}
                barGap="-80%"
              />
            </div>
          </div>
          {footer}
        </div>
      );
    }

    case "content-efficiency": {
      // Line chart showing videos vs GPM
      const timeSeriesData = chartData as TimeSeriesData;
      const transformedData = getTransformedData(timeSeriesData);

      if (transformedData.length === 0) {
        return (
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No data available for this date range</p>
              </div>
            </div>
          </div>
        );
      }

      const config = {
        metrics: ["videos", "gpm"],
        formatters: {
          videos: (v: number) => numberFormatter.format(v),
          gpm: (v: number) => gmvFormatter.format(v),
        },
        colors: {
          videos: "#3b82f6", // blue
          gpm: "#9E77ED", // purple
        },
        labels: {
          videos: "Videos Posted",
          gpm: "GPM",
        },
      };

      return (
        <div className="flex flex-col h-full gap-4">
          <div
            ref={chartElementRef}
            className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4"
          >
            {/* Header with legend left, date/granularity right */}
            {createLineChartHeader(config)}

            {/* Chart */}
            <div className="flex-1 min-h-0">
              <MetricsLineChartView
                data={transformedData as any}
                config={config as any}
                showLegend={false}
                chartType="area"
              />
            </div>
          </div>
          {footer}
        </div>
      );
    }

    case "product-gmv-revenue-source":
    default: {
      // Stacked bar chart for product GMV by revenue source
      const timeSeriesData = chartData as TimeSeriesData;
      const transformedData = getTransformedData(timeSeriesData);

      if (transformedData.length === 0) {
        return (
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No data available for this date range</p>
              </div>
            </div>
          </div>
        );
      }

      const revenueMetrics = ["LIVE", "VIDEO", "PRODUCT_CARD"];

      const config = {
        metrics: revenueMetrics,
        formatters: Object.fromEntries(revenueMetrics.map((m) => [m, (v: number) => gmvFormatter.format(v)])),
        colors: REVENUE_SOURCE_COLORS,
        labels: {
          LIVE: "LIVE",
          VIDEO: "Short Video",
          PRODUCT_CARD: "Product Card",
        },
      };

      return (
        <div className="flex flex-col h-full gap-4">
          <div
            ref={chartElementRef}
            className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4"
          >
            {/* Header with legend left, date/granularity right */}
            {createBarChartHeader(config)}

            {/* Chart */}
            <div className="flex-1 min-h-0">
              <MetricsBarChartView
                data={transformedData as any}
                config={config as any}
                variant="stacked"
                showLegend={false}
              />
            </div>
          </div>
          {footer}
        </div>
      );
    }
  }
};
